import { mergeMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { EMPTY, of } from 'rxjs';
import get from 'lodash.get';

import { NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_INFO } from 'ui-components/services/notification-service';
import { NotificationHubService } from '../../notification-hub/notification-hub-service';
import { NotificationType } from '../../notification-hub/notification-hub.types';
import { notificationDescriptionMessages } from '../../notification-hub/notification-messages';
import { ModalService } from 'ui-components/services/modal-service';
import { ExportOrdersContainer } from '../../../components/modals/export-records-modal/containers/export-orders-container';
import Global from '../../../store/global-selectors';
import { DocumentService } from '../../../services/document-service';
import * as ExportApi from './export-orders.api';
import * as Actions from './export-orders.actions';
import { OrderExportHelper } from './export-helper';
import { messages } from '../messages';

const NOTIFICATION_DURATION_IN_MS = 5000;

export const openExportShipmentsModalEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_EXPORT_ORDERS_MODAL),
    mergeMap(({ filters }) => {
      ModalService.launchModal({
        component: ExportOrdersContainer,
        props: {
          filters,
        },
      });
      return EMPTY;
    }),
  );
};

const showSuccessNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.ORDERS_EXPORT_STARTED,
      defaultTitle: messages.orderExportNotifHeader.defaultMessage,
      defaultDescription: messages.orderExportNotifBody.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_INFO,
      title: <FormattedMessage {...messages.orderExportNotifHeader} />,
      content: <FormattedMessage {...messages.orderExportNotifBody} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

const showFailureNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.ORDERS_EXPORT_FAILED,
      defaultTitle: messages.orderExportErrorHeader.defaultMessage,
      defaultDescription: messages.orderExportErrorBody.defaultMessage,
    },
    {
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.orderExportErrorHeader} />,
      content: <FormattedMessage {...messages.orderExportErrorBody} />,
      duration: NOTIFICATION_DURATION_IN_MS,
    },
  );
};

export const initiateOrdersExportEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.EXPORT_ORDERS_REQUEST),
    mergeMap((action) => {
      const { viewRecordsOrSelectedRecords, primaryRecordsOrLineItems, searchTerm, filters, isOnAdvancedSearch, intl } = action;
      const state = getState();

      const { initiatedByUserId, requestUrl, requestBody } = OrderExportHelper.getExportRequestParameters(
        state,
        viewRecordsOrSelectedRecords,
        primaryRecordsOrLineItems,
        searchTerm,
        filters,
        isOnAdvancedSearch,
        intl,
        history,
      );
      return ExportApi.exportOrders(requestUrl, requestBody).pipe(
        mergeMap((jobId) => {
          showSuccessNotification();
          return of(Actions.exportOrdersSuccess(jobId, initiatedByUserId));
        }),
        catchError(() => {
          showFailureNotification();
          return EMPTY;
        }),
      );
    }),
  );
};

export const initiateOrdersCustomExportEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.CUSTOM_EXPORT_ORDERS_REQUEST),
    mergeMap((action) => {
      const { viewRecordsOrSelectedRecords, extractId, selectedOrderIds, searchTerm, filters, isOnAdvancedSearch, intl } = action;
      const state = getState();
      const { requestBody, initiatedByUserId } = OrderExportHelper.getCustomExportRequestBody({
        state,
        viewRecordsOrSelectedRecords,
        extractId,
        selectedOrderIds,
        searchTerm,
        contextFilters: filters,
        isOnAdvancedSearch,
        intl,
        history,
      });

      return ExportApi.customExportOrders(requestBody).pipe(
        mergeMap(({ jobGuids }) => {
          showSuccessNotification();
          const jobId = jobGuids && jobGuids[0];
          return of(Actions.exportOrdersSuccess(jobId, initiatedByUserId));
        }),
        catchError(() => {
          showFailureNotification();
          return EMPTY;
        }),
      );
    }),
  );
};

export const downloadOrdersExportEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.EXPORT_ORDERS_COMPLETE),
    mergeMap((action) => {
      const state = getState();
      const downloadUrl = get(action, 'data.DownloadUrl') || get(action, 'data.downloadUrl');
      const jobId = get(action, 'messageGuid');
      const job = Global.selectExportOrdersExportOrdersJobByJobId(state, {
        jobId,
      });
      const currentUserId = Global.selectUserCurrentUserId(state);
      const initiatedByUserId = get(job, 'initiatedByUserId');
      const errorMessage = get(action, 'data.errorMessage', '');
      if (errorMessage && errorMessage.length) {
        showFailureNotification();
        return of(Actions.exportOrdersCleanup(jobId));
      }
      if (currentUserId === initiatedByUserId) {
        DocumentService.getExportDocumentActions(downloadUrl);
        NotificationHubService.showNotification(
          {
            notificationType: NotificationType.ORDERS_EXPORT_COMPLETED,
            defaultTitle: messages.orderExportNotifDoneHeader.defaultMessage,
            defaultDescription: notificationDescriptionMessages.ORDERS_EXPORT_COMPLETED.defaultMessage,
            metadataJson: JSON.stringify({ downloadUrl }),
          },
          {
            notificationType: NOTIFICATION_TYPE_INFO,
            title: <FormattedMessage {...messages.orderExportNotifDoneHeader} />,
            content: <FormattedMessage {...messages.orderExportNotifDoneBody} />,
            duration: NOTIFICATION_DURATION_IN_MS,
          },
        );
      }
      return of(Actions.exportOrdersCleanup(jobId));
    }),
  );
};
